import { IConfig } from '@microsoft/applicationinsights-common';
import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import JwtDecode from 'jwt-decode';
import { cloudRoleNameTelemetryInitializer, healthChecksFilter, staticFileFilter } from 'telemetry-initializers';

interface Settings {
  instrumentationKey: string;
  cloudRoleName: string;
  token?: string;
}

enum Claims {
  Id = 'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier',
  CompanyId = 'http://schemas.plan2learn.dk/2018/01/identity/claims/company/id'
}
let defaultOptions: IConfig = {
  enableCorsCorrelation: true,
  enableRequestHeaderTracking: true,
  enableResponseHeaderTracking: true,
  enableAutoRouteTracking: true,
  autoTrackPageVisitTime: true,
  correlationHeaderExcludedDomains: ['adressevaelger.dk', '*.adressevaelger.dk']
};

export const configureApplicationInsights = (settings: Settings, configure: (options: IConfig) => void, telemetryInitializers: any[]) => {
  try {
    if (!settings.cloudRoleName) {
      throw 'You must provide cloudRoleName as a setting';
    }
    if (!settings.instrumentationKey) {
      throw 'You must provide instrumentationKey as a setting';
    }

    let options = { ...defaultOptions };

    if (configure) {
      configure(options);
    }

    // Unioned (not overwritten like other options) so an app's own exclusions can't drop the library defaults
    options.correlationHeaderExcludedDomains = Array.from(new Set([
      ...(defaultOptions.correlationHeaderExcludedDomains ?? []),
      ...(options.correlationHeaderExcludedDomains ?? [])
    ]));

    let appInsights = new ApplicationInsights({
      config: {
        instrumentationKey: settings.instrumentationKey,
        ...options
      }
    });

    appInsights.addTelemetryInitializer(cloudRoleNameTelemetryInitializer(settings.cloudRoleName));

    try {
      if (settings.token) {
        var jwtToken = JwtDecode<Record<Claims, string | number>>(settings.token);

        var key = '';
        if (jwtToken[Claims.Id]) {
          key = 'user-' + jwtToken[Claims.Id];
        } else {
          key = 'company-' + jwtToken[Claims.CompanyId];
        }

        appInsights.setAuthenticatedUserContext(key);
      }
    } catch (ex) {
      appInsights.trackException({ exception: ex as any });
    }

    appInsights.addTelemetryInitializer(healthChecksFilter);
    appInsights.addTelemetryInitializer(staticFileFilter);

    if (telemetryInitializers) {
      for (let i = 0; i < telemetryInitializers.length; i++) {
        const initializer = telemetryInitializers[i];
        appInsights.addTelemetryInitializer(initializer);
      }
    }

    appInsights.loadAppInsights();

    appInsights.trackPageView();
  } catch (ex) {
    console.error('AI init error', ex);
  }
};
